import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { axiosInstance } from '../../services/axiosInstance';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import { formatDateTimeKst } from '../../utils/dateUtils';

type PoolStreamRow = {
  pool_id: number;
  code: string;
  name: string;
  webrtc_ready: boolean;
  central_active_count: number;
  central_slot_count: number;
  agent_stale: boolean;
  agent_age_sec: number | null;
  whip_ts_buffer_bytes: number | null;
  whip_forwarding: boolean | null;
  whip_base_set: boolean | null;
  whip_base_host: string;
  edge_pipeline: string;
  gateway_nvr_bypass: boolean;
  gateway_stream_width: number | null;
  agent: { reported_at?: string } | null;
};

type StreamStatusBody = {
  central_go2rtc_available: boolean;
  updated_at?: string;
  ui_guide?: string;
  pools: PoolStreamRow[];
};

type ArtifactWindow = { nal?: number; decode?: number; stutter?: number };

type ViewerWindow = {
  lost?: number;
  recv?: number;
  dropped?: number;
  decoded?: number;
  freeze?: number;
  issue?: number;
  samples?: number;
  loss_pct?: number;
  jitter_ms_avg?: number;
};

type ArtifactInfo = {
  recent?: boolean;
  nal_total?: number;
  decode_total?: number;
  stutter_total?: number;
  window_sec?: number;
  windows?: {
    m5?: ArtifactWindow;
    m15?: ArtifactWindow;
    m60?: ArtifactWindow;
    since_reset?: ArtifactWindow;
  };
  edge_windows?: {
    m5?: ArtifactWindow;
    m15?: ArtifactWindow;
    m60?: ArtifactWindow;
    since_reset?: ArtifactWindow;
  };
  viewer_windows?: {
    m5?: ViewerWindow;
    m15?: ViewerWindow;
    m60?: ViewerWindow;
    since_reset?: ViewerWindow;
  };
  gateway?: {
    nal_delta?: number;
    decode_delta?: number;
    stutter_delta?: number;
    last_error?: string;
  };
  central_probe?: {
    nal_last?: number;
    decode_last?: number;
    stutter_last?: number;
    probed_at?: number;
    snippet?: string;
    ok?: boolean;
  };
};

type EdgeLiveInfo = {
  ok?: boolean;
  active?: boolean;
  recv_kbps?: number | null;
  packets_per_sec?: number | null;
  codec?: string;
  nal_delta?: number;
  decode_delta?: number;
  stutter_delta?: number;
  last_error?: string;
  sampled_at_epoch?: number;
};

type ViewerLiveInfo = {
  t?: number;
  lost?: number;
  recv?: number;
  dropped?: number;
  decoded?: number;
  freeze?: number;
  jitter_ms?: number;
  recv_kbps?: number;
  interval_sec?: number;
  issue?: number;
};

type ArtifactWindowKey = 'm5' | 'm15' | 'm60' | 'since_reset';

function pickArtifactWindow(
  a: ArtifactInfo | undefined,
  key: ArtifactWindowKey,
  side: 'central' | 'edge' = 'central'
): { nal: number; decode: number; stutter: number } {
  const bag = side === 'edge' ? a?.edge_windows : a?.windows;
  const w = bag?.[key];
  if (w) {
    return {
      nal: w.nal ?? 0,
      decode: w.decode ?? 0,
      stutter: w.stutter ?? 0,
    };
  }
  if (side === 'central') {
    return {
      nal: a?.nal_total ?? 0,
      decode: a?.decode_total ?? 0,
      stutter: a?.stutter_total ?? 0,
    };
  }
  return { nal: 0, decode: 0, stutter: 0 };
}

function pickViewerWindow(
  a: ArtifactInfo | undefined,
  key: ArtifactWindowKey
): {
  lost: number;
  dropped: number;
  freeze: number;
  issue: number;
  loss_pct: number;
  jitter_ms_avg: number;
  samples: number;
} {
  const w = a?.viewer_windows?.[key];
  return {
    lost: w?.lost ?? 0,
    dropped: w?.dropped ?? 0,
    freeze: w?.freeze ?? 0,
    issue: w?.issue ?? 0,
    loss_pct: w?.loss_pct ?? 0,
    jitter_ms_avg: w?.jitter_ms_avg ?? 0,
    samples: w?.samples ?? 0,
  };
}

type QualityChannel = {
  stream: string;
  cctv_id: number;
  cctv_code?: string;
  cctv_name?: string;
  label: string;
  health: string;
  active: boolean;
  recv_kbps: number | null;
  packets_per_sec: number | null;
  bytes_recv: number;
  packets: number;
  producer_n: number;
  consumer_n: number;
  consumers?: Array<{
    kind: string;
    format: string;
    protocol: string;
    remote_addr: string;
    user_agent: string;
  }>;
  format: string;
  codec: string;
  remote_addr: string;
  medias: string;
  kbps_spark: Array<number | null>;
  pps_spark: Array<number | null>;
  edge_live?: EdgeLiveInfo;
  viewer_live?: ViewerLiveInfo;
  artifact?: ArtifactInfo;
  history?: Array<{
    t: number;
    kbps: number | null;
    pps: number | null;
    health: string;
    active: boolean;
  }>;
};

type QualityPool = {
  pool_id: number;
  code: string;
  name: string;
  recv_mbps: number;
  active_channels: number;
  stall_channels: number;
  weak_channels: number;
  down_channels: number;
  nal_events?: number;
  decode_events?: number;
  stutter_events?: number;
  channel_count: number;
  channels: QualityChannel[];
};

type QualityBody = {
  central_go2rtc_available: boolean;
  sample_interval_sec: number;
  sampled_at?: string | null;
  sample_age_sec?: number | null;
  artifact_reset_at?: string | null;
  artifact_window_sec?: number;
  stall_kbps_threshold?: number;
  weak_kbps_threshold?: number;
  ui_guide?: string;
  totals: {
    recv_mbps: number;
    active_channels: number;
    stall_channels: number;
    weak_channels: number;
    down_channels: number;
    slot_channels: number;
  };
  history_totals: Array<{
    t: number;
    recv_mbps: number;
    active: number;
    stall: number;
    weak: number;
    down: number;
  }>;
  pools: QualityPool[];
};

function formatTsBuffer(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '—';
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(bytes % 1048576 === 0 ? 0 : 1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function formatAge(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '—';
  if (sec < 60) return `${Math.round(sec)}초 전`;
  if (sec < 3600) return `${Math.round(sec / 60)}분 전`;
  return `${Math.round(sec / 3600)}시간 전`;
}

function formatKbps(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)} Mbps`;
  return `${v.toFixed(0)} kbps`;
}

function healthChip(health: string) {
  const map: Record<string, { color: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
    ok: { color: 'success', label: '정상' },
    weak: { color: 'warning', label: '약함' },
    stall: { color: 'error', label: '정지' },
    down: { color: 'default', label: '미수신' },
  };
  const m = map[health] || { color: 'default' as const, label: health || '—' };
  return <Chip size="small" color={m.color} label={m.label} />;
}

function SparkBars({ values, color }: { values: Array<number | null>; color: string }) {
  const nums = values.filter((v): v is number => v != null && Number.isFinite(v));
  const max = Math.max(1, ...nums);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height: 28, minWidth: 72 }}>
      {values.slice(-40).map((v, i) => {
        const h = v == null || !Number.isFinite(v) ? 2 : Math.max(2, Math.round((v / max) * 26));
        return (
          <Box
            key={i}
            sx={{
              width: 2,
              height: h,
              bgcolor: v == null ? 'action.disabled' : color,
              opacity: v == null ? 0.3 : 0.85,
            }}
          />
        );
      })}
    </Box>
  );
}

/**
 * brand-manager: 시설별 WHIP + 중앙 수신 품질(채널·그래프).
 */
const BrandManagerGatewayStreamMonitorSection: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [body, setBody] = useState<StreamStatusBody | null>(null);
  const [quality, setQuality] = useState<QualityBody | null>(null);
  const [expandPool, setExpandPool] = useState<number | null>(null);
  const [detailStream, setDetailStream] = useState<string | null>(null);
  const [detailHistory, setDetailHistory] = useState<QualityChannel['history']>([]);
  const [artifactWindow, setArtifactWindow] = useState<ArtifactWindowKey>('m5');
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statusRes, qualityRes] = await Promise.all([
        axiosInstance.get('/api/brand-menu/gateway-stream-status'),
        axiosInstance.get('/api/brand-menu/gateway-stream-quality', {
          params: { history: true },
        }),
      ]);
      const data = statusRes.data;
      setBody({
        central_go2rtc_available: Boolean(data?.central_go2rtc_available),
        updated_at: data?.updated_at,
        ui_guide: typeof data?.ui_guide === 'string' ? data.ui_guide : undefined,
        pools: Array.isArray(data?.pools) ? data.pools : [],
      });
      setQuality(qualityRes.data || null);
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '게이트웨이 스트림 상태 조회 실패'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPoolDetail = useCallback(async (poolId: number) => {
    try {
      const { data } = await axiosInstance.get('/api/brand-menu/gateway-stream-quality', {
        params: { pool_id: poolId, history: true },
      });
      const pool = Array.isArray(data?.pools)
        ? data.pools.find((p: QualityPool) => p.pool_id === poolId)
        : null;
      if (!pool) return;
      setQuality((prev) => {
        if (!prev) return data;
        const exists = prev.pools.some((p) => p.pool_id === poolId);
        return {
          ...prev,
          totals: data.totals || prev.totals,
          history_totals: data.history_totals || prev.history_totals,
          pools: exists
            ? prev.pools.map((p) => (p.pool_id === poolId ? pool : p))
            : [...prev.pools, pool],
        };
      });
    } catch {
      /* 폴링 본문과 병합 실패 시 무시 */
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
    const id = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(id);
  }, [open, load]);

  const pools = body?.pools || [];
  const qPools = quality?.pools || [];
  const totals = quality?.totals;

  const windowLabel =
    artifactWindow === 'm5'
      ? '5분'
      : artifactWindow === 'm15'
        ? '15분'
        : artifactWindow === 'm60'
          ? '60분'
          : '리셋후';

  const poolWindowSums = useMemo(() => {
    let nal = 0;
    let decode = 0;
    let stutter = 0;
    let edgeNal = 0;
    let edgeDecode = 0;
    let edgeStutter = 0;
    let viewerLost = 0;
    let viewerDropped = 0;
    let viewerIssue = 0;
    for (const p of qPools) {
      for (const ch of p.channels || []) {
        const w = pickArtifactWindow(ch.artifact, artifactWindow, 'central');
        const e = pickArtifactWindow(ch.artifact, artifactWindow, 'edge');
        const v = pickViewerWindow(ch.artifact, artifactWindow);
        nal += w.nal;
        decode += w.decode;
        stutter += w.stutter;
        edgeNal += e.nal;
        edgeDecode += e.decode;
        edgeStutter += e.stutter;
        viewerLost += v.lost;
        viewerDropped += v.dropped;
        viewerIssue += v.issue;
      }
    }
    return {
      nal,
      decode,
      stutter,
      edgeNal,
      edgeDecode,
      edgeStutter,
      viewerLost,
      viewerDropped,
      viewerIssue,
    };
  }, [qPools, artifactWindow]);

  const totalChart = useMemo(() => {
    const rows = quality?.history_totals || [];
    return rows.map((h) => ({
      t: h.t,
      label: h.t ? new Date(h.t * 1000).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '',
      recv_mbps: h.recv_mbps,
      active: h.active,
      stall: h.stall,
      weak: h.weak,
      down: h.down,
    }));
  }, [quality?.history_totals]);

  const onTogglePool = (poolId: number) => {
    setExpandPool((cur) => {
      const next = cur === poolId ? null : poolId;
      if (next != null) void loadPoolDetail(next);
      return next;
    });
    setDetailStream(null);
    setDetailHistory([]);
  };

  const onResetArtifacts = useCallback(async () => {
    setResetting(true);
    setError('');
    try {
      await axiosInstance.post('/api/brand-menu/gateway-stream-quality/reset');
      await load();
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '이상 카운터 초기화 실패'));
    } finally {
      setResetting(false);
    }
  }, [load]);

  const onSelectChannel = (ch: QualityChannel) => {
    setDetailStream((cur) => {
      if (cur === ch.stream) {
        setDetailHistory([]);
        return null;
      }
      setDetailHistory(ch.history || []);
      return ch.stream;
    });
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
          시설 WebRTC · 중앙 수신 품질
        </Typography>
        <Button size="small" onClick={() => void load()} disabled={loading || !open}>
          새로고침
        </Button>
        <Button
          size="small"
          color="warning"
          variant="outlined"
          onClick={() => void onResetArtifacts()}
          disabled={resetting || loading || !open}
        >
          이상카운터 초기화
        </Button>
        {loading || resetting ? <CircularProgress size={16} /> : null}
        <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label="펼치기">
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, mb: open ? 1 : 0 }}>
        활성 시설. NAL/디코드/툭툭은 시간창 기준(기본 5분). 15초마다 갱신.
      </Typography>
      <Collapse in={open}>
        {error ? (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        {/* —— 수신 품질 요약 —— */}
        <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 1, mb: 0.5 }}>
          중앙 수신 품질 (전체)
        </Typography>
        {quality?.ui_guide ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            {quality.ui_guide}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            size="small"
            color={quality?.central_go2rtc_available ? 'success' : 'warning'}
            label={quality?.central_go2rtc_available ? '중앙 go2rtc 기동' : '중앙 go2rtc 미기동'}
          />
          {quality?.sampled_at ? (
            <Chip
              size="small"
              variant="outlined"
              label={`샘플 ${formatDateTimeKst(quality.sampled_at)} (${formatAge(quality.sample_age_sec)})`}
            />
          ) : (
            <Chip size="small" variant="outlined" label="샘플 대기(기동 후 수 초)" />
          )}
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`합계 ${totals?.recv_mbps != null ? `${Number(totals.recv_mbps).toFixed(2)} Mbps` : '—'}`}
          />
          <Chip size="small" label={`수신 ${totals?.active_channels ?? '—'}/${totals?.slot_channels ?? '—'}`} />
          <Chip
            size="small"
            color={(totals?.stall_channels || 0) > 0 ? 'error' : 'default'}
            label={`정지 ${totals?.stall_channels ?? 0}`}
          />
          <Chip
            size="small"
            color={(totals?.weak_channels || 0) > 0 ? 'warning' : 'default'}
            label={`약함 ${totals?.weak_channels ?? 0}`}
          />
          <Chip size="small" label={`미수신 ${totals?.down_channels ?? 0}`} />
          <Chip
            size="small"
            variant={artifactWindow === 'm5' ? 'filled' : 'outlined'}
            color={artifactWindow === 'm5' ? 'primary' : 'default'}
            label="5분"
            onClick={() => setArtifactWindow('m5')}
          />
          <Chip
            size="small"
            variant={artifactWindow === 'm15' ? 'filled' : 'outlined'}
            color={artifactWindow === 'm15' ? 'primary' : 'default'}
            label="15분"
            onClick={() => setArtifactWindow('m15')}
          />
          <Chip
            size="small"
            variant={artifactWindow === 'm60' ? 'filled' : 'outlined'}
            color={artifactWindow === 'm60' ? 'primary' : 'default'}
            label="60분"
            onClick={() => setArtifactWindow('m60')}
          />
          <Chip
            size="small"
            variant={artifactWindow === 'since_reset' ? 'filled' : 'outlined'}
            color={artifactWindow === 'since_reset' ? 'primary' : 'default'}
            label="리셋후"
            onClick={() => setArtifactWindow('since_reset')}
          />
          <Chip
            size="small"
            color={poolWindowSums.edgeNal > 0 ? 'error' : 'default'}
            label={`현장NAL(${windowLabel}) ${poolWindowSums.edgeNal}`}
          />
          <Chip
            size="small"
            color={poolWindowSums.nal > 0 ? 'error' : 'default'}
            label={`중앙NAL(${windowLabel}) ${poolWindowSums.nal}`}
          />
          <Chip
            size="small"
            color={poolWindowSums.edgeDecode > 0 ? 'warning' : 'default'}
            label={`현장디코드 ${poolWindowSums.edgeDecode}`}
          />
          <Chip
            size="small"
            color={poolWindowSums.decode > 0 ? 'warning' : 'default'}
            label={`중앙디코드 ${poolWindowSums.decode}`}
          />
          <Chip
            size="small"
            color={poolWindowSums.stutter > 0 || poolWindowSums.edgeStutter > 0 ? 'warning' : 'default'}
            label={`툭툭 현/중 ${poolWindowSums.edgeStutter}/${poolWindowSums.stutter}`}
          />
          <Chip
            size="small"
            color={
              poolWindowSums.viewerLost > 0 || poolWindowSums.viewerIssue > 0
                ? 'error'
                : 'default'
            }
            label={`관제손실(${windowLabel}) ${poolWindowSums.viewerLost}`}
          />
          <Chip
            size="small"
            color={poolWindowSums.viewerDropped > 0 ? 'warning' : 'default'}
            label={`관제드롭 ${poolWindowSums.viewerDropped}`}
          />
          {quality?.artifact_reset_at ? (
            <Chip
              size="small"
              variant="outlined"
              label={`리셋 ${formatDateTimeKst(quality.artifact_reset_at)}`}
            />
          ) : null}
        </Box>

        {totalChart.length > 1 ? (
          <Box sx={{ width: '100%', height: 220, mb: 2 }}>
            <ResponsiveContainer>
              <LineChart data={totalChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" minTickGap={40} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="mbps" tick={{ fontSize: 11 }} width={48} />
                <YAxis yAxisId="cnt" orientation="right" tick={{ fontSize: 11 }} width={36} />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="mbps"
                  type="monotone"
                  dataKey="recv_mbps"
                  name="수신 Mbps"
                  stroke="#1976d2"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="cnt"
                  type="monotone"
                  dataKey="active"
                  name="수신 채널"
                  stroke="#2e7d32"
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="cnt"
                  type="monotone"
                  dataKey="stall"
                  name="정지"
                  stroke="#d32f2f"
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="cnt"
                  type="monotone"
                  dataKey="weak"
                  name="약함"
                  stroke="#ed6c02"
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            시계열 수집 중 — 샘플 2회 이상이면 그래프 표시
          </Typography>
        )}

        {/* —— 시설별 품질 —— */}
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>시설·채널별 수신</Typography>
        {qPools.length === 0 && !loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            품질 샘플 없음 (중앙 스트림 또는 샘플러 대기)
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={36} />
                  <TableCell>시설</TableCell>
                  <TableCell>수신 Mbps</TableCell>
                  <TableCell>수신/채널</TableCell>
                  <TableCell>정지</TableCell>
                  <TableCell>약함</TableCell>
                  <TableCell>미수신</TableCell>
                  <TableCell>현장NAL</TableCell>
                  <TableCell>중앙NAL</TableCell>
                  <TableCell>관제손실</TableCell>
                  <TableCell>현장/중앙 디코드</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {qPools.map((row) => {
                  const openRow = expandPool === row.pool_id;
                  return (
                    <React.Fragment key={row.pool_id}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => onTogglePool(row.pool_id)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {openRow ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.name || row.code || `#${row.pool_id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.code} · id {row.pool_id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {Number(row.recv_mbps || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {row.active_channels}/{row.channel_count}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={row.stall_channels > 0 ? 'error' : 'default'}
                            label={row.stall_channels}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={row.weak_channels > 0 ? 'warning' : 'default'}
                            label={row.weak_channels}
                          />
                        </TableCell>
                        <TableCell>{row.down_channels}</TableCell>
                        <TableCell>
                          {(() => {
                            let n = 0;
                            for (const ch of row.channels || []) {
                              n += pickArtifactWindow(ch.artifact, artifactWindow, 'edge').nal;
                            }
                            return (
                              <Chip
                                size="small"
                                color={n > 0 ? 'error' : 'default'}
                                label={n}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            let n = 0;
                            for (const ch of row.channels || []) {
                              n += pickArtifactWindow(ch.artifact, artifactWindow, 'central').nal;
                            }
                            return (
                              <Chip
                                size="small"
                                color={n > 0 ? 'error' : 'default'}
                                label={n}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            let n = 0;
                            for (const ch of row.channels || []) {
                              n += pickViewerWindow(ch.artifact, artifactWindow).lost;
                            }
                            return (
                              <Chip
                                size="small"
                                color={n > 0 ? 'error' : 'default'}
                                label={n}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            let e = 0;
                            let c = 0;
                            for (const ch of row.channels || []) {
                              e += pickArtifactWindow(ch.artifact, artifactWindow, 'edge').decode;
                              c += pickArtifactWindow(ch.artifact, artifactWindow, 'central').decode;
                            }
                            return (
                              <Chip
                                size="small"
                                color={e > 0 || c > 0 ? 'warning' : 'default'}
                                label={`${e}/${c}`}
                              />
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                      {openRow ? (
                        <TableRow>
                          <TableCell colSpan={11} sx={{ bgcolor: 'action.hover', py: 1.5 }}>
                            <Box sx={{ overflowX: 'auto' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>채널</TableCell>
                                    <TableCell>상태</TableCell>
                                    <TableCell>수신</TableCell>
                                    <TableCell>pkt/s</TableCell>
                                    <TableCell>현장 / 중앙 / 관제 ({windowLabel})</TableCell>
                                    <TableCell>코덱</TableCell>
                                    <TableCell>형식</TableCell>
                                    <TableCell>원격</TableCell>
                                    <TableCell>kbps 추이</TableCell>
                                    <TableCell>소비자(누구)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.channels.map((ch) => {
                                    const selected = detailStream === ch.stream;
                                    const chChart = (selected ? detailHistory || ch.history : ch.history) || [];
                                    const chChartData = chChart.map((h) => ({
                                      label: h.t
                                        ? new Date(h.t * 1000).toLocaleTimeString('ko-KR', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                          })
                                        : '',
                                      kbps: h.kbps,
                                      pps: h.pps,
                                    }));
                                    const code = ch.cctv_code || ch.label;
                                    const nm = ch.cctv_name;
                                    const detailTitle =
                                      nm && nm !== code
                                        ? `${code} (${nm}) · ${ch.stream}`
                                        : `${code || ch.stream} · ${ch.stream}`;
                                    return (
                                      <React.Fragment key={ch.stream}>
                                        <TableRow
                                          hover
                                          selected={selected}
                                          sx={{ cursor: 'pointer' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onSelectChannel(ch);
                                          }}
                                        >
                                          <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {ch.cctv_code || ch.label || `cctv-${ch.cctv_id}`}
                                              {ch.cctv_name &&
                                              ch.cctv_name !== (ch.cctv_code || '') ? (
                                                <Typography
                                                  component="span"
                                                  variant="body2"
                                                  sx={{ fontWeight: 500, ml: 0.75 }}
                                                  color="text.secondary"
                                                >
                                                  ({ch.cctv_name})
                                                </Typography>
                                              ) : null}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                              채널 {ch.stream}
                                              {ch.cctv_id != null ? ` · id ${ch.cctv_id}` : ''}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>{healthChip(ch.health)}</TableCell>
                                          <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                            {formatKbps(ch.recv_kbps)}
                                          </TableCell>
                                          <TableCell sx={{ fontFamily: 'monospace' }}>
                                            {ch.packets_per_sec != null
                                              ? ch.packets_per_sec.toFixed(1)
                                              : '—'}
                                          </TableCell>
                                          <TableCell sx={{ minWidth: 150 }}>
                                            {(() => {
                                              const a = ch.artifact || {};
                                              const central = pickArtifactWindow(
                                                a,
                                                artifactWindow,
                                                'central'
                                              );
                                              const edge = pickArtifactWindow(
                                                a,
                                                artifactWindow,
                                                'edge'
                                              );
                                              const viewer = pickViewerWindow(a, artifactWindow);
                                              const el = ch.edge_live || {};
                                              const vl = ch.viewer_live || {};
                                              const gw = a.gateway || {};
                                              const cp = a.central_probe || {};
                                              const edgeKbps =
                                                el.recv_kbps != null && Number.isFinite(Number(el.recv_kbps))
                                                  ? formatKbps(Number(el.recv_kbps))
                                                  : '—';
                                              const viewerKbps =
                                                vl.recv_kbps != null && Number.isFinite(Number(vl.recv_kbps))
                                                  ? formatKbps(Number(vl.recv_kbps))
                                                  : '—';
                                              return (
                                                <>
                                                  <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                                                    현장 {edgeKbps} · NAL {edge.nal} / 디코드 {edge.decode} / 툭툭{' '}
                                                    {edge.stutter}
                                                  </Typography>
                                                  <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                                                    중앙 {formatKbps(ch.recv_kbps)} · NAL {central.nal} / 디코드{' '}
                                                    {central.decode} / 툭툭 {central.stutter}
                                                  </Typography>
                                                  <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                                                    관제 {viewerKbps} · 손실 {viewer.lost} / 드롭 {viewer.dropped}{' '}
                                                    / freeze {viewer.freeze} / 이슈 {viewer.issue}
                                                    {viewer.samples > 0
                                                      ? ` · jitter ${viewer.jitter_ms_avg}ms · 손실률 ${viewer.loss_pct}%`
                                                      : ''}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                  >
                                                    Δ현장 {el.nal_delta ?? 0}/{el.decode_delta ?? 0}/
                                                    {el.stutter_delta ?? 0}
                                                    {' · '}
                                                    Δwhip {gw.nal_delta ?? 0}/{gw.decode_delta ?? 0}/
                                                    {gw.stutter_delta ?? 0}
                                                    {' · '}
                                                    중앙프로브 {cp.nal_last ?? 0}/{cp.decode_last ?? 0}/
                                                    {cp.stutter_last ?? 0}
                                                    {' · '}
                                                    Δ관제 {vl.lost ?? 0}/{vl.dropped ?? 0}/{vl.freeze ?? 0}
                                                  </Typography>
                                                  {el.last_error || gw.last_error || cp.snippet ? (
                                                    <Typography
                                                      variant="caption"
                                                      color="error.main"
                                                      display="block"
                                                      sx={{ wordBreak: 'break-all' }}
                                                    >
                                                      {(
                                                        el.last_error ||
                                                        gw.last_error ||
                                                        cp.snippet ||
                                                        ''
                                                      ).slice(0, 100)}
                                                    </Typography>
                                                  ) : null}
                                                  {Object.keys(el).length === 0 ? (
                                                    <Typography variant="caption" color="warning.main" display="block">
                                                      현장 품질 미보고 — DataGateway_new.exe 갱신 후 하트비트 확인
                                                    </Typography>
                                                  ) : null}
                                                  {viewer.samples === 0 && Object.keys(vl).length === 0 ? (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                      관제 미보고 — 통합관제에서 해당 채널 WHEP 재생 시 수집
                                                    </Typography>
                                                  ) : null}
                                                </>
                                              );
                                            })()}
                                          </TableCell>
                                          <TableCell>{ch.codec || '—'}</TableCell>
                                          <TableCell>{ch.format || '—'}</TableCell>
                                          <TableCell>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                              {ch.remote_addr || '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <SparkBars
                                              values={ch.kbps_spark || []}
                                              color={
                                                ch.health === 'stall'
                                                  ? '#d32f2f'
                                                  : ch.health === 'weak'
                                                    ? '#ed6c02'
                                                    : '#1976d2'
                                              }
                                            />
                                          </TableCell>
                                          <TableCell sx={{ minWidth: 140, maxWidth: 260 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {ch.consumer_n ?? 0}명
                                            </Typography>
                                            {(ch.consumers || []).length === 0 ? (
                                              <Typography variant="caption" color="text.secondary">
                                                없음
                                              </Typography>
                                            ) : (
                                              (ch.consumers || []).map((c, i) => (
                                                <Box key={`${ch.stream}-c-${i}`} sx={{ mt: 0.25 }}>
                                                  <Typography variant="caption" display="block">
                                                    {c.kind || c.format || '수신'}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                    sx={{
                                                      fontFamily: 'monospace',
                                                      wordBreak: 'break-all',
                                                    }}
                                                  >
                                                    {[c.remote_addr, c.protocol || c.format]
                                                      .filter(Boolean)
                                                      .join(' · ') || '—'}
                                                  </Typography>
                                                  {c.user_agent ? (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      display="block"
                                                      sx={{ wordBreak: 'break-all' }}
                                                    >
                                                      {c.user_agent.length > 64
                                                        ? `${c.user_agent.slice(0, 64)}…`
                                                        : c.user_agent}
                                                    </Typography>
                                                  ) : null}
                                                </Box>
                                              ))
                                            )}
                                          </TableCell>
                                        </TableRow>
                                        {selected ? (
                                          <TableRow>
                                            <TableCell
                                              colSpan={10}
                                              sx={{ bgcolor: 'background.paper', py: 1.5, px: 2 }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                채널 상세 · {detailTitle}
                                              </Typography>
                                              {chChartData.length > 1 ? (
                                                <Box sx={{ width: '100%', height: 200 }}>
                                                  <ResponsiveContainer>
                                                    <LineChart data={chChartData}>
                                                      <CartesianGrid strokeDasharray="3 3" />
                                                      <XAxis
                                                        dataKey="label"
                                                        minTickGap={32}
                                                        tick={{ fontSize: 11 }}
                                                      />
                                                      <YAxis
                                                        yAxisId="kbps"
                                                        tick={{ fontSize: 11 }}
                                                        width={52}
                                                      />
                                                      <YAxis
                                                        yAxisId="pps"
                                                        orientation="right"
                                                        tick={{ fontSize: 11 }}
                                                        width={40}
                                                      />
                                                      <Tooltip />
                                                      <Legend />
                                                      <Line
                                                        yAxisId="kbps"
                                                        type="monotone"
                                                        dataKey="kbps"
                                                        name="kbps"
                                                        stroke="#1976d2"
                                                        dot={false}
                                                        isAnimationActive={false}
                                                      />
                                                      <Line
                                                        yAxisId="pps"
                                                        type="monotone"
                                                        dataKey="pps"
                                                        name="pkt/s"
                                                        stroke="#7b1fa2"
                                                        dot={false}
                                                        isAnimationActive={false}
                                                      />
                                                    </LineChart>
                                                  </ResponsiveContainer>
                                                </Box>
                                              ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                  채널 시계열 부족 — 잠시 후 다시 선택
                                                </Typography>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ) : null}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              채널 행 클릭 시 해당 행 바로 아래에 kbps·pkt/s 그래프 (다시 클릭하면 닫음)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* —— 기존 WHIP/설정 요약 —— */}
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
          WHIP 버퍼 · 현장 보고
        </Typography>
        {body?.ui_guide ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {body.ui_guide}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            size="small"
            color={body?.central_go2rtc_available ? 'success' : 'warning'}
            label={body?.central_go2rtc_available ? '중앙 go2rtc 기동' : '중앙 go2rtc 미기동'}
          />
          {body?.updated_at ? (
            <Chip
              size="small"
              variant="outlined"
              label={`조회 ${formatDateTimeKst(body.updated_at)}`}
            />
          ) : null}
        </Box>
        {pools.length === 0 && !loading && !error ? (
          <Typography variant="body2" color="text.secondary">
            활성 시설 없음
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>시설</TableCell>
                  <TableCell>WebRTC</TableCell>
                  <TableCell>활성/슬롯</TableCell>
                  <TableCell>WHIP ts_buffer</TableCell>
                  <TableCell>WHIP 전송</TableCell>
                  <TableCell>파이프</TableCell>
                  <TableCell>원본 전송</TableCell>
                  <TableCell>재인코딩 폭</TableCell>
                  <TableCell>보고</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pools.map((row) => (
                  <TableRow key={row.pool_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.name || row.code || `#${row.pool_id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.code} · id {row.pool_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.webrtc_ready ? 'success' : 'default'}
                        label={row.webrtc_ready ? '수신' : '없음'}
                      />
                    </TableCell>
                    <TableCell>
                      {row.central_active_count}/{row.central_slot_count}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {formatTsBuffer(row.whip_ts_buffer_bytes)}
                      {row.whip_base_host ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.whip_base_set ? row.whip_base_host : 'WHIP 주소 없음'}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {row.whip_forwarding == null
                        ? '—'
                        : row.whip_forwarding
                          ? '동작'
                          : '없음'}
                    </TableCell>
                    <TableCell>{row.edge_pipeline || '—'}</TableCell>
                    <TableCell>
                      {row.gateway_nvr_bypass ? (
                        <>
                          <Typography variant="body2">ON (copy)</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            재인코딩 없음
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">OFF</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            게이트웨이 재인코딩
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.gateway_nvr_bypass ? (
                        <>
                          <Typography variant="body2">미적용</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            저장값{' '}
                            {row.gateway_stream_width != null
                              ? `${row.gateway_stream_width}p`
                              : '—'}
                          </Typography>
                        </>
                      ) : row.gateway_stream_width != null ? (
                        `${row.gateway_stream_width}p`
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {row.agent == null ? (
                        <Typography variant="caption" color="text.secondary">
                          미보고
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color={row.agent_stale ? 'warning.main' : 'text.secondary'}
                        >
                          {formatAge(row.agent_age_sec)}
                          {row.agent_stale ? ' (오래됨)' : ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default BrandManagerGatewayStreamMonitorSection;
